/**
 * ChatPill — persistent bottom-right floating trigger for the candidate AI chat.
 *
 * Used on:
 *   - /candidates/:id  → opens the candidate-scoped chat panel directly
 *   - /interviews/:id  → opens the SAME candidate-scoped chat (when the
 *                        interview is linked), or prompts to link when orphan
 *
 * Single source of truth for chat — there is NO interview-only chat.
 * The interview-detail entry point is just a different doorway into the
 * candidate's chat context. Avoids two stores of "what was said" answers
 * giving different replies to the same question.
 *
 * Copy discipline (Phase 4c legal-spine carryover): audit/search language
 * only — "Ask what was said", "Search interviews". Never "AI insights",
 * "Evaluate candidate", or any insights-shaped framing.
 */
import { useState } from "react";
import { MessageSquareText, Link2 } from "lucide-react";
import CandidateChatPanel from "./CandidateChatPanel";

const STROKE = 1.5;

export default function ChatPill({
  candidateId,             // null when the interview is orphaned
  initialDraft,            // optional — pre-populate the first message
  orphanMode = false,      // truthy when no candidate_id available
  onOrphanRequestLink,     // handler when orphan pill is clicked
}) {
  const [open, setOpen] = useState(false);

  if (orphanMode) {
    // Amber variant — clicking calls the parent's handler (typically opens
    // the LinkCandidateDialog on InterviewDetail). We do NOT open the chat
    // panel because there's no candidate context yet.
    return (
      <button
        onClick={() => {
          if (typeof onOrphanRequestLink === "function") onOrphanRequestLink();
        }}
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-full border border-amber-800 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-900 shadow-lg shadow-amber-900/10 transition hover:bg-amber-100"
        data-testid="chat-pill-orphan"
        title="Link this interview to a candidate to enable chat"
      >
        <Link2 className="h-4 w-4" strokeWidth={STROKE} />
        Link to candidate to enable chat
      </button>
    );
  }

  if (!candidateId) {
    // No candidateId AND no orphanMode — render nothing. Defensive.
    return null;
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-full bg-emerald-800 px-4 py-3 text-[13px] font-medium text-white shadow-lg shadow-emerald-900/20 transition hover:bg-emerald-900 hover:shadow-xl"
        data-testid="chat-pill"
        title="Ask what was said in this candidate's interviews"
      >
        <MessageSquareText className="h-4 w-4" strokeWidth={STROKE} />
        Ask what was said
      </button>
      <CandidateChatPanel
        candidateId={candidateId}
        open={open}
        onClose={() => setOpen(false)}
        initialDraft={initialDraft}
      />
    </>
  );
}
